/** @jsx jsx */
import { jsx } from '@emotion/react';
import { TypeRampV2 } from '@smartsheet/lodestar-core';
import { DragEventHandler } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  ExistingGraphNodeItem,
  GraphNodeTypes,
  NewGraphNodeItem,
  selectGrabbedItem,
  setSelectedElementID,
} from '../../../../services/ducks/designerDuck/designerDuck';
import { selectModuleById } from '../../../../services/ducks/modulesDuck/modulesDuck';
import {
  insertNodeIntoGraph,
  moveNode,
} from '../../../../services/ducks/workflowDuck/workflowDuck';
import { NODE_HEIGHT } from '../../../../utils/dimensions';
import { DropTargetContainer } from './styled';
import { generateFourDigitNumber, isGrabbedItemExisting } from './utils';

// A State has a target handle and a source handle
// eslint-disable-next-line
export const DropTarget = ({ data, selected }: NodeProps) => {
  const { t: translate } = useTranslation();

  const grabbedItem = useSelector(selectGrabbedItem);
  const module = useSelector(
    selectModuleById(grabbedItem?.moduleID ?? '', grabbedItem?.pluginUUID ?? '')
  );
  const dispatch = useDispatch();

  const generateName = (
    grabbedItem: NewGraphNodeItem | ExistingGraphNodeItem
  ) => {
    if (!grabbedItem.state) {
      let generatedName = '';
      switch (grabbedItem.type) {
        case GraphNodeTypes.STATE:
          generatedName = `State ${generateFourDigitNumber()}`;
          break;
        case GraphNodeTypes.CHILD:
          generatedName = `Child Workflow ${generateFourDigitNumber()}`;
          break;
        case GraphNodeTypes.JUNCTION:
          generatedName = `Junction ${generateFourDigitNumber()}`;
          break;
        default:
          generatedName = `${generateFourDigitNumber()}`;
      }
      return generatedName;
    }
    return grabbedItem.state;
  };

  const dropHandler: DragEventHandler<HTMLDivElement> = event => {
    event?.preventDefault();
    if (!grabbedItem) return;
    const insertPayload = {
      parentID: data.prevId,
      name: generateName(grabbedItem),
      nodeToInsert: grabbedItem,
      module,
    };

    if (isGrabbedItemExisting(grabbedItem)) {
      dispatch(moveNode(insertPayload));
    } else {
      dispatch(insertNodeIntoGraph(insertPayload));
    }
    dispatch(setSelectedElementID(grabbedItem.id));
  };

  return (
    <DropTargetContainer
      onDrop={dropHandler}
      onDragEnter={undefined}
      onDragOver={event => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={true}
        style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
      />
      <TypeRampV2 system="lodestar" variant="bodyStrong">
        {grabbedItem && isGrabbedItemExisting(grabbedItem)
          ? translate('designer:canvas.moveTargetPlaceholder')
          : translate('designer:canvas.dropTargetPlaceholder')}
      </TypeRampV2>
    </DropTargetContainer>
  );
};
